import React, { useState, useEffect } from 'react';
import { Modal, Title, Text, Badge, Divider, ScrollArea, Flex, Card, Button, Loader } from '@mantine/core';
import { Row, Col, Label, Input, Modal as RSModal, ModalHeader, ModalBody } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getActivityStatusList } from '../../../slices/activityStatus/thunk';
import { toast } from 'react-toastify';
import { updateBreachRecordApi } from '../../../helpers/breach_record/api_helper';
import { uploadFiles as uploadFilesApi } from '../../../helpers/file_manager/backend_helper';
import { getFile, downloadFile } from '../../../slices/fileManager/thunk';
import { getComputedActivityStatus } from '../utils/taskHelpers';

const getUserDisplayName = user =>
	user?.full_name ||
	[user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
	user?.name ||
	user?.username ||
	'';

const normalizeStr = s => (s || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();

const getActivityStatusBadgeColor = label => {
	const l = normalizeStr(label);
	if (l.includes('aprobado')) return 'green';
	if (l.includes('rechazado')) return 'red';
	if (l.includes('pendiente')) return 'yellow';
	if (l.includes('revision') || l.includes('resvision')) return 'blue';
	return 'gray';
};

const getImpactoBadgeColor = label => {
	const l = (label || '').toLowerCase();
	if (l.includes('alto')) return 'red';
	if (l.includes('medio')) return 'yellow';
	if (l.includes('bajo')) return 'teal';
	return 'gray';
};

const getRiesgoBadgeColor = label => {
	const l = (label || '').toLowerCase();
	if (l.includes('alto')) return 'red';
	if (l.includes('medio')) return 'yellow';
	if (l.includes('bajo')) return 'green';
	return 'gray';
};

const normalizeFileIds = fileValue => {
	const rawValues = Array.isArray(fileValue)
		? fileValue
		: fileValue
			? [fileValue]
			: [];

	return rawValues
		.map(item => {
			if (!item) return null;
			if (typeof item === 'string') return item;
			return item._id || item.id || null;
		})
		.filter(Boolean);
};

const dedupeSelectedFiles = files => {
	const uniqueFiles = [];
	const seen = new Set();

	files.forEach(file => {
		const key = [file.name, file.size, file.lastModified].join(':');
		if (seen.has(key)) return;
		seen.add(key);
		uniqueFiles.push(file);
	});

	return uniqueFiles;
};

const BreachRecordDetailModal = ({ opened, onClose, breach, onSave }) => {
	const dispatch = useDispatch();
	const { activityStatusListSelect } = useSelector(state => state.ActivityStatus);
	const [observation, setObservation] = useState('');
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [observationError, setObservationError] = useState(false);
	const [existingFiles, setExistingFiles] = useState([]);
	const [fileLoading, setFileLoading] = useState(false);
	const [fileActionLoading, setFileActionLoading] = useState(null);
	const [fileError, setFileError] = useState(false);
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
	const [previewFile, setPreviewFile] = useState(null);

	useEffect(() => {
		if (activityStatusListSelect.length === 0) {
			dispatch(getActivityStatusList());
		}
	}, []);

	useEffect(() => {
		if (opened && breach) {
			setObservation(breach.observation || '');
			setSelectedFiles([]);
			setExistingFiles([]);
			setObservationError(false);
			setFileError(false);
		}
	}, [opened, breach]);

	useEffect(() => {
		const fileIds = normalizeFileIds(breach?.file_id);

		if (!opened || fileIds.length === 0) {
			setExistingFiles([]);
			return;
		}

		setFileLoading(true);
		Promise.all(
			fileIds.map(fileId =>
				dispatch(getFile(fileId))
					.unwrap()
					.then(file => ({ ...file, _id: file?._id || file?.id || fileId }))
			)
		)
			.then(files => setExistingFiles(files.filter(Boolean)))
			.catch(() => setExistingFiles([]))
			.finally(() => setFileLoading(false));
	}, [opened, breach?.file_id, dispatch]);

	if (!breach) return null;

	const computedStatus = getComputedActivityStatus(breach);
	const impactoLabel = breach.breach_impact?.name || '';
	const riesgoLabel = breach.breach_risk_classification?.name || '';
	const breachDate = breach.breach_date
		? new Date(breach.breach_date).toLocaleDateString('es-CL')
		: 'No especificada';
	const hasAnyEvidence = existingFiles.length > 0 || selectedFiles.length > 0;

	const handleFileChange = e => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		setSelectedFiles(prev => dedupeSelectedFiles([...prev, ...files]));
		if (fileError) setFileError(false);
		e.target.value = '';
	};

	const handleRemoveExistingFile = fileId => {
		setExistingFiles(prev => prev.filter(file => (file._id || file.id) !== fileId));
	};

	const handleRemoveSelectedFile = fileToRemove => {
		setSelectedFiles(prev => prev.filter(file => file !== fileToRemove));
	};

	const handleDownloadFile = async (fileObj, fileId) => {
		const resolvedFileId = fileId || fileObj?._id || fileObj?.id;
		if (!resolvedFileId || fileActionLoading === `download:${resolvedFileId}`) return;
		setFileActionLoading(`download:${resolvedFileId}`);
		try {
			const blob = await dispatch(downloadFile({ file_id: resolvedFileId })).unwrap();
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = fileObj?.filename || fileObj?.name || 'archivo';
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		} finally {
			setFileActionLoading(null);
		}
	};

	const handlePreviewClick = async (fileObj, fileId) => {
		if (fileObj instanceof File) {
			try {
				const fileURL = URL.createObjectURL(fileObj);
				const previewContent = fileObj.type?.includes('text') ? await fileObj.text() : null;
				setPreviewFile({
					filename: fileObj.name,
					mime_type: fileObj.type,
					content: previewContent,
					url: fileURL,
				});
				setIsPreviewModalOpen(true);
			} catch (error) {
				console.error('Error al previsualizar el archivo local:', error);
			}
			return;
		}

		const resolvedFileId = fileId || fileObj?._id || fileObj?.id;
		if (!resolvedFileId) return;
		try {
			const blob = await dispatch(downloadFile({ file_id: resolvedFileId, disable_msg: true })).unwrap();
			if (!(blob instanceof Blob)) throw new Error('El archivo no es válido.');
			const fileURL = URL.createObjectURL(blob);
			setPreviewFile({ ...fileObj, url: fileURL });
			setIsPreviewModalOpen(true);
		} catch (error) {
			console.error('Error al obtener el archivo:', error);
		}
	};

	const handleClosePreview = () => {
		if (previewFile?.url) URL.revokeObjectURL(previewFile.url);
		setPreviewFile(null);
		setIsPreviewModalOpen(false);
	};

	const handleSave = async () => {
		if (!observation.trim()) {
			setObservationError(true);
		}
		if (!hasAnyEvidence) {
			setFileError(true);
		}
		if (!observation.trim() || !hasAnyEvidence) {
			return;
		}
		setIsSaving(true);
		try {
			const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
			const modificationUser = authUser?.username || authUser?.email || authUser?._id || 'system';

			let fileIds = existingFiles
				.map(file => file?._id || file?.id)
				.filter(Boolean);

			if (selectedFiles.length > 0) {
				try {
					const formData = new FormData();
					selectedFiles.forEach(file => {
						formData.append('files', file);
					});
					const uploaded = await uploadFilesApi({ folder_id: null, formData });
					const list = Array.isArray(uploaded)
						? uploaded
						: Array.isArray(uploaded?.data)
							? uploaded.data
							: uploaded?._id || uploaded?.id
								? [uploaded]
								: [];
					const uploadedIds = list.map(item => item._id || item.id).filter(Boolean);
					if (uploadedIds.length === 0) {
						toast.error('No se pudieron registrar los archivos adjuntos', { autoClose: 3000 });
						return;
					}
					fileIds = [...fileIds, ...uploadedIds];
				} catch (uploadError) {
					console.error('Error uploading file:', uploadError);
					toast.error('Error al subir los archivos adjuntos', { autoClose: 3000 });
					return;
				}
			}

			let searchList = activityStatusListSelect;
			if (searchList.length === 0) {
				const fetched = await dispatch(getActivityStatusList()).unwrap();
				searchList = (fetched || []).map(item => ({ value: item._id, label: item.name || '' }));
			}
			const normalizeStr = s => (s || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
			const enRevisionStatus = searchList.find(item => {
				const n = normalizeStr(item.label);
				return n.includes('revision') || n.includes('resvision');
			});

			await updateBreachRecordApi({
				_id: breach._id,
				observation,
				file_id: fileIds,
				activity_status_id: enRevisionStatus?.value || breach.activity_status_id,
				modification_user: modificationUser,
			});

			toast.success('Incumplimiento actualizado con éxito', { autoClose: 3000 });
			onSave?.();
		} catch (error) {
			console.error('Error saving breach record:', error);
			toast.error('Error al guardar el incumplimiento', { autoClose: 3000 });
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<>
		<Modal
			opened={opened}
			onClose={onClose}
			size='80%'
			padding='lg'
			centered
			radius={8}
			transitionProps={{ transition: 'fade', duration: 200 }}
			scrollAreaComponent={ScrollArea.Autosize}
			title={
				<Text fw={600} size='lg'>Detalle de la actividad</Text>
			}
		>
			<Flex gap='md'>
				{/* Left column */}
				<Card style={{ flex: 2 }}>
					<Card.Section inheritPadding mt='sm'>
						<Row>
							<Col lg={12} className='mb-3'>
								<Label className='form-label'>Descripción</Label>
								<Input
									type='textarea'
									rows='3'
									value={breach.description || ''}
									disabled
									readOnly
								/>
							</Col>

							<Col lg={12} className='mb-3'>
								<Label className='form-label'>Medida Correctiva</Label>
								<Input
									type='textarea'
									rows='3'
									value={breach.corrective_measure || ''}
									disabled
									readOnly
								/>
							</Col>

							<Col lg={12} className='mb-3'>
									<Label className='form-label'>Observación <span className='text-danger'>*</span></Label>
								<Input
									type='textarea'
									rows='4'
									value={observation}
									onChange={(e) => { setObservation(e.target.value); if (observationError) setObservationError(false); }}
									placeholder='Escriba una observación...'
									maxLength={2000}
									invalid={observationError}
								/>
								{observationError && <div className='invalid-feedback'>Este campo es obligatorio.</div>}
							</Col>

							<Col lg={12} className='mb-3'>
								<Label className='form-label'>Evidencias (archivos) <span className='text-danger'>*</span></Label>
								<Input
									type='file'
									multiple
									onChange={handleFileChange}
									invalid={fileError}
								/>
								{fileError && <div className='invalid-feedback'>Este campo es obligatorio.</div>}
								{fileLoading ? (
									<Flex justify='center' py='sm'><Loader size='sm' /></Flex>
								) : (
									<>
										{existingFiles.map(file => {
											const fileId = file._id || file.id;
											return (
												<div key={`existing-${fileId}`} className='d-flex align-items-center gap-2 border rounded px-3 py-2 mb-2'>
													<i className='ri-file-line'></i>
													<span className='flex-grow-1 small'>{file.filename}</span>
													<Badge color='blue' variant='light' size='xs'>Adjunto</Badge>
													<Button
														size='xs'
														variant='light'
														loading={fileActionLoading === `download:${fileId}`}
														onClick={() => handleDownloadFile(file, fileId)}
													>
														Descargar
													</Button>
													<Button size='xs' variant='light' onClick={() => handlePreviewClick(file, fileId)}>
														Previsualizar
													</Button>
													<Button size='xs' color='red' variant='light' onClick={() => handleRemoveExistingFile(fileId)}>
														Eliminar
													</Button>
												</div>
											);
										})}
										{selectedFiles.map(file => {
											const fileKey = [file.name, file.size, file.lastModified].join(':');
											return (
												<div key={`selected-${fileKey}`} className='d-flex align-items-center gap-2 border rounded px-3 py-2 mb-2'>
													<i className='ri-file-upload-line text-success'></i>
													<span className='flex-grow-1 small text-success'>{file.name}</span>
													<Button size='xs' variant='light' onClick={() => handlePreviewClick(file)}>
														Previsualizar
													</Button>
													<Button size='xs' color='red' variant='light' onClick={() => handleRemoveSelectedFile(file)}>
														Eliminar
													</Button>
												</div>
											);
										})}
									</>
								)}
								
							</Col>
						</Row>
					</Card.Section>
				</Card>

				{/* Right column */}
				<Card style={{ flex: 1 }} mt='md'>
					<Card.Section inheritPadding>
						<Title order={5} mb='sm' c='dark'>Características del Incumplimiento</Title>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Estado</Text>
							<Badge color={getActivityStatusBadgeColor(computedStatus)} variant='light' size='sm' tt='capitalize'>
								{computedStatus}
							</Badge>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Fecha</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-calendar' style={{ fontSize: '14px', color: '#666' }}></i>
								<Text size='sm'>{breachDate}</Text>
							</Flex>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Tipo Fiscalización</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-clipboard-check' style={{ fontSize: '14px', color: '#666' }}></i>
								<Text size='sm'>{breach.inspection_types?.name || 'No especificado'}</Text>
							</Flex>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Área Responsable</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-users' style={{ fontSize: '14px', color: '#666' }}></i>
								<Text size='sm'>{breach.breach_responsible_area?.name || 'No especificada'}</Text>
							</Flex>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Responsable</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-user' style={{ fontSize: '14px', color: '#666' }}></i>
								<Text size='sm'>{getUserDisplayName(breach.responsible_user) || 'No asignado'}</Text>
							</Flex>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Categoría</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-tag' style={{ fontSize: '14px', color: '#666' }}></i>
								<Text size='sm'>{breach.breach_category?.name || 'No especificada'}</Text>
							</Flex>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Tipo de Evidencia</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-clipboard' style={{ fontSize: '14px', color: '#666' }}></i>
								<Text size='sm'>{breach.breach_type_of_evidence?.name || 'No especificado'}</Text>
							</Flex>
						</Flex>

						<Divider my='md' />

						<Title order={5} mb='sm' c='dark'>Información Adicional</Title>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Impacto</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-trending-up' style={{ fontSize: '14px', color: '#f57c00' }}></i>
								{impactoLabel ? (
									<Badge color={getImpactoBadgeColor(impactoLabel)} variant='light' size='xs' tt='capitalize'>
										{impactoLabel}
									</Badge>
								) : (
									<Text size='sm' c='dimmed'>No especificado</Text>
								)}
							</Flex>
						</Flex>

						<Flex justify='space-between' align='center' py='xs'>
							<Text size='sm' c='dimmed'>Riesgo</Text>
							<Flex align='center' gap='xs'>
								<i className='icon-alert-triangle' style={{ fontSize: '14px', color: '#d32f2f' }}></i>
								{riesgoLabel ? (
									<Badge color={getRiesgoBadgeColor(riesgoLabel)} variant='light' size='xs' tt='capitalize'>
										{riesgoLabel}
									</Badge>
								) : (
									<Text size='sm' c='dimmed'>No especificado</Text>
								)}
							</Flex>
						</Flex>

						<Divider my='md' />

						<Button
						fullWidth
						loading={isSaving}
						onClick={handleSave}
						disabled={computedStatus === 'Aprobado' || computedStatus === 'En revisión'}
					>
						Guardar
					</Button>
					</Card.Section>
				</Card>
			</Flex>
		</Modal>
		<RSModal
			isOpen={isPreviewModalOpen}
			toggle={handleClosePreview}
			centered={false}
			className='w-100 p-4'
			fullscreen={true}
			scrollable={false}
		>
			<ModalHeader toggle={handleClosePreview}>
				Previsualización
			</ModalHeader>
			<ModalBody className='overflow-hidden'>
				{previewFile && previewFile.url ? (
					previewFile.mime_type?.includes('image') ? (
						<img
							src={previewFile.url}
							alt={previewFile.filename}
							className='img-fluid'
						/>
					) : previewFile.mime_type?.includes('pdf') ? (
						<iframe src={previewFile.url} width='100%' height='100%' title='preview' />
					) : previewFile.mime_type?.includes('text') ? (
						<pre>{previewFile.content || 'No se pudo cargar el contenido'}</pre>
					) : (
						<p>Formato no compatible para previsualización.</p>
					)
				) : (
					<p>Cargando...</p>
				)}
			</ModalBody>
		</RSModal>
		</>
	);
};

export default BreachRecordDetailModal;
